import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CalendarCheck, MessageCircleQuestion, Film, ArrowRight, Sparkles, Check } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const CustomerTestimonialPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero - matching homepage dark hero style */}
      <section className="relative pt-[72px] overflow-hidden bg-[#0A0A12]">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A12] via-[#0d1224] to-[#0A0A12]" />
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1200px] h-[600px] bg-blue-600/8 rounded-full blur-[120px]" />
          <div className="absolute top-1/4 right-0 w-[600px] h-[600px] bg-indigo-600/5 rounded-full blur-[100px]" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-20 pb-24">
          <div className="text-center max-w-4xl mx-auto">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="text-5xl md:text-6xl lg:text-[4.25rem] font-bold leading-[1.08] tracking-tight text-white mb-6"
            >
              Submit a{' '}
              <span className="bg-gradient-to-r from-blue-400 via-blue-300 to-indigo-400 bg-clip-text text-transparent">
                Customer Testimonial
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="text-lg md:text-xl text-white/60 max-w-2xl mx-auto leading-relaxed mb-10"
            >
              AI has a phenomenal ability to have a positive impact on the lives of patients and providers.
              Help other offices undergo an AI transformation and spread the impact by sharing your experience.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.45 }}
              className="flex flex-wrap gap-4 justify-center"
            >
              <Link
                to="/customer-testimonial/submit"
                className="inline-flex items-center justify-center px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-base rounded-full transition-all duration-200 shadow-lg shadow-blue-600/25 hover:shadow-xl hover:shadow-blue-600/30"
              >
                Get Started
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How It Works - light section matching AgentsSection bg-gray-50 */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mb-14">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 text-blue-600 text-sm font-semibold mb-6">
              <Sparkles className="w-3.5 h-3.5" />
              <span>How It Works</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-5 leading-[1.1] tracking-tight">
              Share your story, <span className="text-blue-600">earn $500</span>
            </h2>
            <p className="text-lg text-gray-500 leading-relaxed">
              If you've had a positive experience with Asha, you can submit a customer testimonial in exchange for a short video.
              We'll pay you <span className="text-gray-900 font-medium">$500</span> if your testimonial is accepted. Submitting one is very easy.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                icon: CalendarCheck,
                step: '01',
                title: 'Book a 15-minute call',
                description: 'Pick a time that works for you. Our team will hop on a quick video call at your convenience.',
              },
              {
                icon: MessageCircleQuestion,
                step: '02',
                title: 'Answer a few questions',
                description: 'Our team will ask you some questions during the call. Just share your honest experience with Asha.',
              },
              {
                icon: Film,
                step: '03',
                title: 'We create the testimonial',
                description: 'We\'ll make a polished clip from your responses with professional Asha-branded graphics and motion design.',
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.25, delay: index * 0.08 }}
                className="group relative bg-white rounded-2xl border border-gray-100 hover:border-gray-200 hover:shadow-lg transition-all duration-300 overflow-hidden p-6"
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center bg-blue-50 group-hover:bg-blue-100 transition-colors">
                    <item.icon className="w-5 h-5 text-blue-600" />
                  </div>
                  <span className="px-2.5 py-1 rounded-full bg-gray-50 text-gray-400 text-[11px] font-semibold border border-gray-100 uppercase tracking-wider">
                    Step {item.step}
                  </span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA - matching PricelessCtaSection style */}
      <section className="py-24 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-700 text-white min-h-[400px] flex flex-col items-center justify-center text-center px-6 py-20"
          >
            <div className="absolute inset-0 overflow-hidden">
              <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-blue-400/20 rounded-full blur-[120px] -translate-y-1/2" />
              <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-indigo-400/20 rounded-full blur-[120px] translate-y-1/2" />
            </div>

            <div className="relative z-10 max-w-4xl mx-auto space-y-8">
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1]">
                Help spread the{' '}
                <span className="text-blue-200">impact of AI in dentistry</span>
              </h2>

              <p className="text-lg md:text-xl text-blue-100/80 max-w-2xl mx-auto">
                Book a 15-minute call with our team. We'll handle the rest.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-6">
                <Link
                  to="/customer-testimonial/submit"
                  className="inline-flex items-center justify-center px-10 py-5 bg-white text-blue-600 rounded-full font-bold text-lg shadow-xl hover:shadow-2xl hover:scale-[1.02] transition-all duration-200 gap-2 group"
                >
                  Get Started
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>

              <div className="pt-8 flex flex-wrap items-center justify-center gap-8 text-blue-100/60 text-sm font-semibold tracking-wide">
                {['15-MINUTE CALL', '$500 PAYMENT', 'WE HANDLE EDITING'].map(item => (
                  <div key={item} className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-blue-300" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CustomerTestimonialPage;
